import { Outlet, NavLink } from 'react-router-dom';
import {
  Activity,
  FileText,
  Grid3x3,
  Table2,
  DollarSign,
  Receipt,
  AlertCircle,
  Download,
  LogOut,
  AlertTriangle,
  Package,
  Sparkles,
  CheckCircle,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

const navigation = [
  { name: 'Pipeline Health', href: '/pipeline-health', icon: Activity, live: true },
  { name: 'Stuck Queue', href: '/stuck', icon: AlertTriangle, live: true },
  { name: 'Coverage Matrix', href: '/coverage', icon: Table2, live: true },
  { name: 'Ingestion Status', href: '/ingestion', icon: Package, live: true },
  { name: 'Cost per Batch', href: '/costs/batches', icon: Receipt, live: true },
  { name: 'PDF Tracker', href: '/pdfs', icon: FileText, live: false },
  { name: 'Model Coverage', href: '/models', icon: Grid3x3, live: false },
  { name: 'Cost & Volume', href: '/costs', icon: DollarSign, live: false },
  { name: 'Unmapped Center', href: '/unmapped', icon: AlertCircle, live: false },
  { name: 'Export Center', href: '/export', icon: Download, live: false },
  { name: 'Auto-Onboarding', href: '/auto-onboarding', icon: Sparkles, live: true },
  { name: 'Load Status', href: '/load-status', icon: CheckCircle, live: true },
];

export default function Layout() {
  const { user, signOut } = useAuth();

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Skip-to-content link — invisible until focused, helps keyboard
          and screen-reader users bypass the sidebar nav. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-gray-900 focus:shadow-lg focus:ring-2 focus:ring-blue-500"
      >
        Skip to main content
      </a>

      {/* Sidebar */}
      <aside
        aria-label="Primary navigation"
        className="w-64 bg-gray-900 text-white flex flex-col"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-gray-800">
          <h1 className="text-xl font-bold">InfiOps</h1>
        </div>

        {/* Navigation */}
        <nav aria-label="Page sections" className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/auto-onboarding'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span className="flex-1">{item.name}</span>
              {!item.live && (
                <span
                  className="text-[10px] uppercase tracking-wide text-gray-400"
                  aria-label="Coming soon"
                >
                  soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="border-t border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-jcb-yellow flex items-center justify-center text-gray-900 font-semibold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-300 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={signOut}
              className="rounded p-2 text-gray-300 transition-colors hover:bg-gray-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 rounded-md bg-jcb-yellow px-3 py-1 font-bold text-gray-900">
              JCB
            </div>
            <div className="h-6 w-px bg-gray-300" aria-hidden="true" />
            <h2 className="text-sm text-gray-600">
              Ingestion Operations Center
            </h2>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-auto p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
